import { AnimatePresence, motion } from 'framer-motion';
import * as React from 'react';
import { useStyles } from '@sravni/react-utils';
import modules_a1e6048f from '../styles/Transition.module.less.js';
import { EASINGS } from './_utils.js';

const hasHeightValue = (value) => value != null && parseInt(value.toString(), 10) > 0;
const variants = {
    exit: (props) => props.withAnimation
        ? {
            ...(props.animateOpacity && {
                opacity: hasHeightValue(props.startingHeight) ? 1 : 0,
            }),
            height: props.startingHeight,
            transition: {
                height: { duration: 0.2, ease: EASINGS.ease },
                opacity: { duration: 0.3, ease: EASINGS.ease },
            },
        }
        : {},
    enter: (props) => props.withAnimation
        ? {
            ...(props.animateOpacity && {
                opacity: 1,
            }),
            height: props.endingHeight,
            transition: {
                height: {
                    duration: 0.3,
                    ease: EASINGS.ease,
                },
                opacity: {
                    duration: 0.4,
                    ease: EASINGS.ease,
                },
            },
            transitionEnd: { display: null },
        }
        : {},
};
const Collapse = React.forwardRef((props, ref) => {
    const { unmountOnExit, visible, enableAnimation = true, animateOpacity = true, startingHeight = 0, endingHeight = 'auto', className, onAnimationComplete, ...rest } = props;
    const cx = useStyles(modules_a1e6048f);
    const fromZeroHeight = startingHeight === 0;
    const [open, setOpen] = React.useState(!!visible);
    const getInitialHidden = () => {
        // If it is open by default, no need to apply `aria-hidden`
        if (visible)
            return false;
        // If startingHeight > 0, then content is partially visible
        if (hasHeightValue(startingHeight))
            return false;
        // Else, the content is hidden
        return true;
    };
    const [display, setDisplay] = React.useState(() => {
        if (!fromZeroHeight)
            return true;
        const hidden = getInitialHidden();
        return !hidden;
    });
    const collapseClassnames = cx(className, 'collapse-overflow', {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'collapse-visible': display === true,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'collapse-hidden': display === false,
    });
    const custom = {
        startingHeight,
        endingHeight,
        animateOpacity,
        withAnimation: enableAnimation,
    };
    const ownProps = {
        ref,
        // @future: set parameter as `definition` when we remove `framer-motion`
        // v3 support
        onAnimationComplete: (definition) => {
            if (definition === 'exit' && !open && fromZeroHeight) {
                setDisplay(false);
            }
            definition && onAnimationComplete?.(definition);
        },
        ...rest,
        variants,
        custom,
    };
    React.useEffect(() => {
        if (visible) {
            setDisplay(true);
        }
        else {
            !enableAnimation && setDisplay(false);
        }
        setOpen(!!visible);
    }, [visible, enableAnimation]);
    if (unmountOnExit) {
        return (React.createElement(AnimatePresence, { initial: false, custom: custom }, visible && (
        // @ts-ignore
        React.createElement(motion.div, { ...ownProps, className: collapseClassnames, initial: "exit", animate: "enter", exit: "exit" }))));
    }
    // @ts-ignore
    return React.createElement(motion.div, { ...ownProps, className: collapseClassnames, initial: false, animate: open ? 'enter' : 'exit' });
});
Collapse.displayName = 'Collapse';

export { Collapse };
//# sourceMappingURL=Collapse.js.map
